/**
 * Склейка трёх ног виджета ARPDAU/ARPMAU (MAU, AdMon, InApps) по периоду.
 *
 * Ноги отдают период в РАЗНОМ формате при groupBy='month': активные
 * пользователи приходят из formatDateTime(activity_period, '%Y-%m') → '2026-07',
 * а выручка — из toStartOfMonth(event_date) → '2026-07-01'. День и неделя у
 * обеих ног уже '%Y-%m-%d' и совпадают как есть.
 */

import { pickNet } from '../config/platformFees';

/** Приводит период любой ноги к общему ключу склейки. */
export function periodKey(period, groupBy) {
  return groupBy === 'month' ? String(period).slice(0, 7) : String(period);
}

function emptyRow(period) {
  return {
    period,
    dau: 0,
    admonAndroid: 0,
    admonIos: 0,
    inappsAndroid: 0,
    inappsIos: 0
  };
}

/**
 * Собирает сырые строки виджета: по строке на период, с разбивкой выручки
 * по платформам. Инапы приходят парой gross + NET (`inappsAndroid` /
 * `inappsAndroidNet`); выбор между ними и сам ARPDAU делает виджет.
 *
 * @param {Object} params
 * @param {Array}  params.dauData    — activeUsersResponse.data
 * @param {Array}  params.admonData  — ответ /metrics/admon-by-period
 * @param {Array}  params.inappsData — ответ /metrics/inapps-by-period
 * @param {string} params.groupBy    — day | week | month
 * @returns {Array} строки, отсортированные по периоду
 */
export function mergeArpdauPeriods({ dauData = [], admonData = [], inappsData = [], groupBy = 'day' }) {
  const periodsMap = new Map();

  dauData.forEach(item => {
    const key = periodKey(item.period, groupBy);
    periodsMap.set(key, { ...emptyRow(key), dau: item.activeUsers || 0 });
  });

  // withNet: строка выручки несёт серверный NET (revenueNet) — копим его в `<поле>Net`.
  // Нет revenueNet (старый кэш сервера) — в NET идёт gross этой строки, константы нет.
  const addRevenue = (rows, androidField, iosField, withNet = false) => {
    rows.forEach(item => {
      const key = periodKey(item.period, groupBy);
      if (!periodsMap.has(key)) {
        periodsMap.set(key, emptyRow(key));
      }
      const periodData = periodsMap.get(key);
      const field = item.platform === 'android' ? androidField
        : item.platform === 'ios' ? iosField : null;
      if (!field) return;

      periodData[field] += item.revenue || 0;
      if (withNet) {
        const netField = `${field}Net`;
        periodData[netField] = (periodData[netField] || 0) + (pickNet(item.revenue, item.revenueNet) || 0);
      }
    });
  };

  addRevenue(admonData, 'admonAndroid', 'admonIos');
  addRevenue(inappsData, 'inappsAndroid', 'inappsIos', true);

  return Array.from(periodsMap.values()).sort((a, b) => a.period.localeCompare(b.period));
}
